import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  chakra,
  useToast,
} from "@chakra-ui/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import NextImage from "next/image";
import { IconChevronDown } from "../../../styles/icons";
import NavBarMobile from "./NavbarMobile";

import * as N from "../../utilities/notifications";
import * as C from "../../utilities/layoutConstants";
import { useMemo, useState } from "react";
import { logout } from "../../authentication/api";
import { visitorMenu, practitionerMenuLong, userMenuLong } from "./menu";
import LogInModal from "../../authentication/LogInModal";
import CustomAvatar from "../../components/common/CustomAvatar";

const NavbarMain = (props, { blockBG = null, blockColor = null }) => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const router = useRouter();
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showTherapistSignupModal, setShowTherapistSignupModal] =
    useState(false);

  const avatarImage = useMemo(
    () =>
      user?.userDetails?.therapistProfile?.profileImage
        ? user?.userDetails?.therapistProfile?.profileImage
        : null,
    [user]
  );

  const useBG = useMemo(() => blockBG ?? "white", [blockBG]);
  const useColor = useMemo(() => blockColor ?? "brand.400", [blockColor]);

  const authBtns = () => (
    <Box display={{ base: "none", lg: "flex" }} alignContent="center">
      <Button
        variant="menu"
        size="sm"
        fontSize="sm"
        colorScheme="tertiary"
        onClick={() => setShowModal(true)}
      >
        Log in
      </Button>

      <Link href="/practitioners/therapist-info" prefetch={false}>
        <Button size="sm" fontSize="sm" variant="solid">
          Therapist sign up
        </Button>
      </Link>
    </Box>
  );

  const showMenu = (arrMenu) => (
    <MenuList>
      {arrMenu.map((item) => (
        <>
          {item.divider && <MenuDivider />}
          {!item.button && (
            <Link href={item.href} prefetch={false}>
              {item.leftIcon ? (
                <MenuItem icon={item.leftIcon} color="brand.300">
                  <Text textStyle={item.size === "small" ? "body2" : "body1"}>
                    {item.label}
                  </Text>
                </MenuItem>
              ) : (
                <MenuItem>
                  <Text textStyle={item.size === "small" ? "body2" : "body1"}>
                    {item.label}
                  </Text>
                </MenuItem>
              )}
            </Link>
          )}
        </>
      ))}
      <MenuItem onClick={() => handleLogout()} color="brand.300">
        <Text textStyle="body2">Log out</Text>
      </MenuItem>
    </MenuList>
  );

  const handleClick = async (dest) => {
    if (dest === "login") {
      setShowModal(true);
      return;
    }
    router.push(`practitioners/therapist-info`);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout(user.userDetails));
      router.push(`/`);

      toast(N.logoutSuccess);
    } catch (error) {
      console.error("ERROR", error);
      toast(N.logoutFail(error.message));
    }
  };

  return (
    <>
      {showModal && (
        <LogInModal title="" onCloseModal={() => setShowModal(false)} />
      )}
      {showTherapistSignupModal && (
        <LogInModal
          title=""
          onCloseModal={() => setShowTherapistSignupModal(false)}
          signUpChoice="therapist"
        />
      )}
      <chakra.nav
        id="header"
        style={{
          backgroundColor: "purple",
        }}
        width="100%"
      >
        <Flex
          px={[2, 6]}
          py={[1, 5]}
          align="center"
          justifyContent="space-between"
          color={useColor}
          bgColor={useBG}
          height={C.NAVBAR_HEIGHT}
        >
          {/*Logo*/}
          <Box style={{ cursor: "pointer" }}>
            <Link href="/" passHref prefetch={false}>
              <Image
                width={{ base: "150px", lg: "247px" }}
                height={{ base: "28px", lg: "46px" }}
                src="/images/homepage/wd-logo.png"
                alt="welldoing logo"
              />
            </Link>
          </Box>

          {/* Nav Items - only shown if =>lg*/}
          <HStack as="nav" spacing="1" display={{ base: "none", lg: "block" }}>
            {visitorMenu.map((item, idx) => {
              if (!item.submenu) {
                return (
                  <Link key={idx} href={item.href} prefetch={false}>
                    <Button
                      variant="menu"
                      fontSize="sm"
                      colorScheme="tertiary"
                      leftIcon={item.leftIcon ? item.leftIcon : ""}
                      aria-label={item.label}
                    >
                      <Text color={useColor}>{item.label}</Text>
                    </Button>
                  </Link>
                );
              }
              return (
                <Menu key={idx}>
                  <MenuButton
                    as={Button}
                    rightIcon={<IconChevronDown />}
                    variant="menu"
                    fontSize="sm"
                    aria-label={item.label}
                    colorScheme="tertiary"
                  >
                    <Text color={useColor}>{item.label}</Text>
                  </MenuButton>
                  <MenuList>
                    {item.submenu.map((submenu, idx) => {
                      if (!submenu.submenu) {
                        return (
                          <Link key={idx} href={submenu.href} prefetch={false}>
                            <Button
                              variant="menu"
                              fontSize="sm"
                              colorScheme="tertiary"
                              leftIcon={
                                submenu.leftIcon ? submenu.leftIcon : ""
                              }
                              aria-label={submenu.label}
                            >
                              <Text color={useColor}>{submenu.label}</Text>
                            </Button>
                          </Link>
                        );
                      }
                      return (
                        <Menu key={idx}>
                          <MenuButton
                            as={Button}
                            rightIcon={<IconChevronDown />}
                            variant="menu"
                            fontSize="sm"
                            aria-label={submenu.label}
                            colorScheme="tertiary"
                          >
                            <Text color={useColor}>{submenu.label}</Text>
                          </MenuButton>
                          <MenuList>
                            {submenu.submenu.map((submenu, idx) => {
                              return (
                                <MenuItem key={idx}>{submenu.label}</MenuItem>
                              );
                            })}
                          </MenuList>
                        </Menu>
                      );
                    })}
                  </MenuList>
                </Menu>
              );
            })}
          </HStack>

          {/* Call to action items only shown if =>lg*/}
          <HStack as="nav" spacing="1" display={{ base: "none", lg: "block" }}>
            {user.JWTToken === null && (
              <Box display="flex" alignContent="center">
                {authBtns()}
              </Box>
            )}
            {/* Show profile options if logged in */}
            {user.JWTToken !== null && (
              <Box display={{ base: "none", lg: "block" }}>
                <Menu>
                  <CustomAvatar
                    user={user.userDetails?.fullname}
                    avatarImage={avatarImage}
                    inMenu
                  />
                  {user.userDetails?.accountType === 1 ? (
                    <>{showMenu(userMenuLong)}</>
                  ) : (
                    <>{showMenu(practitionerMenuLong)}</>
                  )}
                </Menu>
              </Box>
            )}
          </HStack>

          {/* MOBILE - Call to action items if <lg */}
          <Box display={{ base: "block", lg: "none" }}>
            <NavBarMobile
              handleClick={handleClick}
              loggedIn={user.JWTToken !== null}
              handleLogout={handleLogout}
            />
          </Box>
        </Flex>
      </chakra.nav>
    </>
  );
};

export default NavbarMain;

export async function getStaticProps() {
  return {
    props: {},
    revalidate: 24 * 3600,
  };
}
